import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { MapPin, Crosshair, Loader2 } from "lucide-react";

// Fix default marker icons for CRA/Vite
const defaultIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

export const STATUS_COLORS = {
  Occupied: "#10b981",
  Available: "#ef4444",
  Blocked: "#ef4444",
  Reserved: "#f59e0b",
  Maintenance: "#3b82f6",
};

function statusIcon(status) {
  const color = STATUS_COLORS[status] || "#2563eb";
  const html = `
    <div style="position:relative;width:32px;height:32px;display:flex;align-items:center;justify-content:center;">
      <div style="
        position:absolute;inset:0;border-radius:9999px;
        background:${color};opacity:0.2;
        animation:ping 2s cubic-bezier(0,0,0.2,1) infinite;
      "></div>
      <div style="
        width:18px;height:18px;border-radius:9999px;
        background:${color};border:2.5px solid #fff;
        box-shadow:0 2px 8px rgba(0,0,0,0.25);
        position:relative;z-index:1;
      "></div>
    </div>
  `;
  return L.divIcon({ html, className: "", iconSize: [32, 32], iconAnchor: [16, 16] });
}

const selectedIcon = L.divIcon({
  html: `
    <div style="position:relative;width:36px;height:36px;display:flex;align-items:center;justify-content:center;">
      <div style="position:absolute;inset:0;border-radius:9999px;background:#ef4444;opacity:0.2;animation:ping 2s cubic-bezier(0,0,0.2,1) infinite"></div>
      <div style="
        width:20px;height:20px;border-radius:9999px;
        background:#ef4444;border:3px solid #fff;
        box-shadow:0 2px 10px rgba(239,68,68,0.4);
        position:relative;z-index:1;
      "></div>
    </div>
  `,
  className: "",
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

function ClickHandler({ onClick }) {
  useMapEvents({
    click(e) { onClick?.({ lat: e.latlng.lat, lng: e.latlng.lng }); }
  });
  return null;
}

function FlyTo({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center?.[0] != null && center?.[1] != null) {
      map.setView(center, map.getZoom(), { animate: true });
    }
  }, [center, map]);
  return null;
}

function FitToMarkers({ markers }) {
  const map = useMap();
  useEffect(() => {
    const valid = markers.filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng));
    if (valid.length === 0) return;
    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 14, { animate: true });
      return;
    }
    const bounds = L.latLngBounds(valid.map((m) => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [36, 36], maxZoom: 14, animate: true });
  }, [markers, map]);
  return null;
}

function highlightCircle(points, paddingMeters) {
  const valid = points.filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng));
  if (valid.length === 0) return null;
  const center = valid.reduce((acc, m) => ({
    lat: acc.lat + m.lat / valid.length,
    lng: acc.lng + m.lng / valid.length,
  }), { lat: 0, lng: 0 });
  const centerPoint = L.latLng(center.lat, center.lng);
  const farthest = valid.reduce((max, m) => Math.max(max, centerPoint.distanceTo([m.lat, m.lng])), 0);
  return {
    center: [center.lat, center.lng],
    radius: Math.max(farthest + paddingMeters, paddingMeters),
  };
}

export function MapPicker({ value, onChange, height = 340 }) {
  const [locating, setLocating] = useState(false);

  const center = useMemo(() => {
    if (value?.lat && value?.lng) return [value.lat, value.lng];
    return [19.7515, 75.7139]; // Maharashtra center
  }, [value]);

  const handleLocate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange?.({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => setLocating(false)
    );
  };

  return (
    <div className="relative z-0 isolate rounded-2xl overflow-hidden border border-slate-200 shadow-sm" style={{ height }}>
      <MapContainer
        center={center}
        zoom={value?.lat ? 13 : 7}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        <ClickHandler onClick={onChange} />
        {value?.lat && value?.lng && (
          <Marker position={[value.lat, value.lng]} icon={selectedIcon}>
            <Popup className="rounded-xl">
              <div className="text-xs font-semibold text-slate-700">Selected Location</div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
              </div>
            </Popup>
          </Marker>
        )}
        <FlyTo center={value?.lat ? [value.lat, value.lng] : null} />
      </MapContainer>

      {/* Overlay controls */}
      <div className="absolute bottom-4 right-4 z-[400] flex flex-col gap-2">
        <button
          onClick={handleLocate}
          disabled={locating}
          className="w-9 h-9 bg-white rounded-xl shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:text-blue-600 hover:border-blue-200 transition-all duration-150 disabled:opacity-60"
          title="Use my location"
        >
          {locating ? (
            <Loader2 size={15} className="animate-spin" />
          ) : (
            <Crosshair size={15} />
          )}
        </button>
      </div>

      {/* Click hint */}
      {!value?.lat && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[400] bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm border border-slate-200 flex items-center gap-1.5 text-xs text-slate-500 font-medium pointer-events-none">
          <MapPin size={12} className="text-blue-400" />
          Click on the map to pick a location
        </div>
      )}
    </div>
  );
}

export function HoardingsMap({
  markers = [],
  areaHighlightMarkers = [],
  height = 440,
  onSelect,
  onOpen,
}) {
  const center = markers.length ? [markers[0].lat, markers[0].lng] : [19.7515, 75.7139];
  const areaCircle = highlightCircle(areaHighlightMarkers, 700);

  return (
    <div className="relative z-0 isolate rounded-2xl overflow-hidden border border-slate-200 shadow-sm" style={{ height }}>
      <MapContainer
        center={center}
        zoom={markers.length === 1 ? 13 : 6}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        {areaCircle && (
          <Circle
            center={areaCircle.center}
            radius={areaCircle.radius}
            pathOptions={{ color: "#f59e0b", fillColor: "#f59e0b", fillOpacity: 0.12, opacity: 0.7, weight: 2 }}
          />
        )}
        {markers.map((m) => (
          <Marker
            key={m.id}
            position={[m.lat, m.lng]}
            icon={statusIcon(m.status)}
            eventHandlers={{
              click: () => {
                onSelect?.(m);
              }
            }}
          >
            <Popup className="rounded-xl min-w-[160px]">
              <div className="font-semibold text-sm text-slate-800">{m.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{m.city}</div>
              {m.area && <div className="text-xs text-slate-500 mt-0.5">{m.area}</div>}
              <div className="mt-2 flex items-center gap-1.5">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: STATUS_COLORS[m.status] || "#2563eb" }}
                />
                <span className="text-xs font-semibold text-slate-700">{m.status}</span>
              </div>
              {m.price && <div className="mt-1 text-xs font-semibold text-slate-600">{m.price}</div>}
              {onOpen && (
                <button
                  type="button"
                  className="mt-2 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white"
                  onClick={() => onOpen(m)}
                >
                  Open details
                </button>
              )}
            </Popup>
          </Marker>
        ))}
        <FitToMarkers markers={markers} />
      </MapContainer>

      {/* Legend */}
      {markers.length > 0 && (
        <div className="absolute bottom-4 left-4 z-[400] bg-white/90 backdrop-blur-sm rounded-xl border border-slate-200 shadow-md px-3 py-2">
          <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Status</div>
          <div className="flex flex-col gap-1">
            {Object.entries(STATUS_COLORS).map(([label, color]) => (
              <div key={label} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                <span className="text-[11px] text-slate-600 font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
